import { CommonModule } from '@angular/common';
import { Component, ElementRef, HostListener, OnInit, ViewChild, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AuthService } from '../../core/auth.service';
import { BrandingService } from '../../core/branding.service';
import { LayoutService } from '../../core/layout.service';
import { LocaleService, SUPPORTED_LOCALES } from '../../core/locale.service';
import { NotificationInboxItemDto, NotificationInboxService } from '../../core/notification-inbox.service';
import { primaryRole } from '../../core/primary-role';
import { IconComponent } from '../icon/icon.component';

/** Maps NotificationLog.RelatedEntityType (set by NotificationDispatchService callers) to the ops-console
 * route that shows that entity's detail page, so the notification dropdown can link straight to it. Entity
 * types with no detail route of their own (e.g. none yet) are simply omitted - canOpen()/openNotification()
 * treat those, and any related-entity-less notification, as non-interactive. */
export const ENTITY_DETAIL_ROUTES: Record<string, string> = {
  WorkOrder: 'work-orders',
  Asset: 'assets',
  MaintenanceRequest: 'maintenance-requests',
  Store: 'stores',
  PurchaseOrder: 'purchase-orders',
  // An approval request: the id is the workflow task's, and the page opens that task (/my-approvals/<taskId>).
  WorkflowTask: 'my-approvals',
  // A vendor job (calendar jobTask events, docs/design/29 §6); the Job detail route is /jobs/:id.
  Job: 'jobs'
};

@Component({
  selector: 'app-shared-header',
  standalone: true,
  imports: [CommonModule, TranslatePipe, IconComponent],
  templateUrl: './shared-header.component.html',
  styleUrl: './shared-header.component.scss'
})
export class SharedHeaderComponent implements OnInit {
  readonly auth = inject(AuthService);
  readonly branding = inject(BrandingService);
  readonly layout = inject(LayoutService);
  private readonly router = inject(Router);
  private readonly locale = inject(LocaleService);
  private readonly translate = inject(TranslateService);
  private readonly notificationInbox = inject(NotificationInboxService);

  readonly locales = SUPPORTED_LOCALES;
  readonly unreadCount = signal(0);
  readonly isUserMenuOpen = signal(false);
  readonly isLangMenuOpen = signal(false);
  readonly isNotificationsOpen = signal(false);
  readonly recentNotifications = signal<NotificationInboxItemDto[]>([]);
  readonly loadingNotifications = signal(false);

  @ViewChild('userMenu') private userMenuRef?: ElementRef<HTMLElement>;
  @ViewChild('langMenu') private langMenuRef?: ElementRef<HTMLElement>;
  @ViewChild('notificationsMenu') private notificationsMenuRef?: ElementRef<HTMLElement>;

  ngOnInit(): void {
    this.notificationInbox.getUnreadCount().subscribe((result) => this.unreadCount.set(result.count));
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as Node;
    if (this.isUserMenuOpen() && !this.userMenuRef?.nativeElement.contains(target)) {
      this.isUserMenuOpen.set(false);
    }
    if (this.isLangMenuOpen() && !this.langMenuRef?.nativeElement.contains(target)) {
      this.isLangMenuOpen.set(false);
    }
    if (this.isNotificationsOpen() && !this.notificationsMenuRef?.nativeElement.contains(target)) {
      this.isNotificationsOpen.set(false);
    }
  }

  get currentLocale(): string {
    return this.auth.currentUser()?.preferredLocale ?? 'en';
  }

  /** SuperAdmin has no Organization of their own (docs/design/06_multi_tenant_organizations.md §1) - the
   * header falls back to a generic label rather than showing nothing. */
  get organizationLabel(): string {
    return this.auth.currentOrganizationName() ?? this.translate.instant('shared.allOrganizations');
  }

  /** A user can hold several roles; the header only has room for one, so it shows the most privileged (core/primary-role.ts -
   * the stored order means nothing). Display only: this has no effect on access control. */
  get primaryRoleLabel(): string | null {
    const role = primaryRole(this.auth.currentUser()?.roles);
    return role ? this.translate.instant(`shared.roles.${role}`) : null;
  }

  /** No user-photo feature exists yet - initials drawn from the full name are the zero-backend-change
   * stand-in for an avatar. */
  get userInitials(): string {
    const name = this.auth.currentUser()?.fullName ?? '';
    return name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join('');
  }

  toggleUserMenu(): void {
    this.isLangMenuOpen.set(false);
    this.isNotificationsOpen.set(false);
    this.isUserMenuOpen.set(!this.isUserMenuOpen());
  }

  toggleLangMenu(): void {
    this.isUserMenuOpen.set(false);
    this.isNotificationsOpen.set(false);
    this.isLangMenuOpen.set(!this.isLangMenuOpen());
  }

  setLocale(locale: string): void {
    this.isLangMenuOpen.set(false);
    this.locale.apply(locale);
    this.auth.updatePreferredLocale(locale).subscribe();
  }

  /** Opens the dropdown and loads the caller's recent notifications, regardless of unread count - it's
   * also a general "what have I gotten recently" view, not just an unread inbox. Marking read (and
   * clearing the badge) only happens if there actually was something unread; the fetched list's own
   * IsUnread flags are computed before that happens, so items still show which were new. */
  toggleNotifications(): void {
    this.isUserMenuOpen.set(false);
    this.isLangMenuOpen.set(false);
    const opening = !this.isNotificationsOpen();
    this.isNotificationsOpen.set(opening);
    if (!opening) {
      return;
    }

    this.loadingNotifications.set(true);
    this.notificationInbox.getRecent().subscribe({
      next: (items) => {
        this.recentNotifications.set(items);
        this.loadingNotifications.set(false);
      },
      error: () => this.loadingNotifications.set(false)
    });
    if (this.unreadCount() > 0) {
      this.notificationInbox.markAllRead().subscribe(() => this.unreadCount.set(0));
    }
  }

  /** The related record's real reference (its serial code, resolved by the API) - never a fragment of its id. Null when the
   * notification has none or the record is gone. */
  entityRefLabel(item: NotificationInboxItemDto): string | null {
    return item.reference || null;
  }

  /** The notification's title; a template key this build has no text for reads as a generic title, never the raw key. */
  notificationTitle(item: NotificationInboxItemDto): string {
    const key = `shared.notificationKeys.${item.templateKey}`;
    const text = this.translate.instant(key);
    return typeof text === 'string' && text && text !== key ? text : this.translate.instant('shared.notificationKeys.GENERIC');
  }

  canOpen(item: NotificationInboxItemDto): boolean {
    return !!item.relatedEntityType && !!item.relatedEntityId && item.relatedEntityType in ENTITY_DETAIL_ROUTES;
  }

  openNotification(item: NotificationInboxItemDto): void {
    if (!item.relatedEntityType || !item.relatedEntityId) {
      return;
    }
    const route = ENTITY_DETAIL_ROUTES[item.relatedEntityType];
    if (!route) {
      return;
    }
    this.isNotificationsOpen.set(false);
    this.router.navigate([`/${route}`, item.relatedEntityId]);
  }

  logout(): void {
    this.auth.logout();
    this.router.navigate(['/login'], { replaceUrl: true });
  }
}
